import { dec } from '@platform/domain';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ChevronDown, CircleAlert, CircleCheck, Info } from 'lucide-react';
import { forwardRef, type ReactNode } from 'react';
import { issueMessage } from '../../../api/errors';
import { taxTypesQuery } from '../../../api/queries';
import type { ApiIssue, PreviewResult } from '../../../api/types';
import { useI18n } from '../../../i18n/I18nProvider';
import { Money } from '../../../ui/Layout';
import styles from '../Sales.module.css';
import { stepOfField } from './steps';

/** Human label of an issue path (line issues name their line and field). */
export function usePathLabel(currency: string, isNote = false): (path: string) => string {
  const { t } = useI18n();
  return (path: string): string => {
    const line = /^lines\.(\d+)\.(\w+)/.exec(path);
    if (line) {
      const n = Number(line[1]) + 1;
      const field: Record<string, string> = {
        itemId: t.editor.lines.item,
        quantity: t.editor.lines.quantity,
        unitPrice: t.editor.lines.unitPrice(currency),
        amountSold: t.editor.lines.unitPrice(currency),
        weightUnitType: t.editor.lines.weightUnit,
        weightQuantity: t.editor.lines.weightQuantity,
        unitType: t.catalogue.unit,
        discount: t.editor.lines.discount,
        taxIds: t.editor.lines.taxes,
        taxes: t.editor.lines.taxes,
        fixedTaxAmounts: t.editor.lines.taxes,
      };
      return `${t.editor.lines.lineLabel(n)} · ${field[line[2] ?? ''] ?? line[2]}`;
    }
    const map: Record<string, string> = {
      receiver: t.editor.sections.customer,
      'receiver.partyId': t.editor.sections.customer,
      branchId: t.editor.settings.branch,
      activityCode: t.editor.settings.activityCode,
      issueDate: t.editor.settings.issueDate,
      dateTimeIssued: t.editor.settings.issueDate,
      dueDate: t.editor.settings.dueDate,
      serviceDeliveryDate: t.editor.settings.serviceDate,
      currency: t.editor.settings.currency,
      exchangeRate: t.editor.settings.exchangeRate,
      // Block 11 (m-1): a note's lines read «بنود الإشعار», not «بنود الفاتورة», in the rail readiness.
      lines: isNote ? t.editor.steps.noteLines : t.editor.sections.lines,
      extraDiscountAmount: t.editor.totals.extraDiscount,
      // Block 11: the credit cap's issue (`totalAmount:exceeds_reference`); it lives on the lines
      // step and reads «المتبقي القابل للتخفيض: …» rather than the raw path.
      totalAmount: t.editor.note.stillReducible,
      'organization.rin': t.dashboard.steps.rin,
    };
    if (path.startsWith('receiver.address'))
      return `${t.editor.sections.customer} · ${t.address[path.split('.')[2] as keyof typeof t.address] ?? path}`;
    return map[path] ?? path;
  };
}

/**
 * «الفاتورة في لمحة» (BLOCK-1-REDESIGN §A.2): the number chip, the «قبل الإصدار» checks with a fix
 * link per issue, and the D-7 totals. Read only: no input, select or toggle (role 4 RD4-04); it
 * never collapses on desktop.
 */
export const SummaryRail = forwardRef<
  HTMLDivElement,
  {
    orgId: string;
    number: string | null;
    nextNumber: string | null;
    issues: ApiIssue[];
    warnings: ApiIssue[];
    result: PreviewResult | undefined;
    currency: string;
    amountDueForeign: string | null;
    onIssue: (issue: ApiIssue) => void;
    /**
     * Block 11 (role-4 B-1): a credit note's due box flips to «المستحق للمشتري / Due to buyer», the
     * on-screen half of the PDF's due box (D-9, DEC-112). The value is unchanged. Omitted (false)
     * for invoices and debit notes, which keep «المستحق على المشتري».
     */
    isCredit?: boolean;
    /** Block 11 (m-1): a note's rail heading reads «الإشعار في لمحة» and its lines «بنود الإشعار». */
    isNote?: boolean;
    /**
     * Block 11 (AC-N2): a note's reference-step title. When set, the rail shows the reference step
     * ONLY as its name, its open-issue count and a link — never as a `references:*` message, and
     * never as a control that chooses the reference. Omitted for invoices (no reference step).
     */
    referenceStepLabel?: string;
    className?: string;
  }
>(function SummaryRail(
  {
    orgId,
    number,
    nextNumber,
    issues,
    warnings,
    result,
    currency,
    amountDueForeign,
    onIssue,
    isCredit = false,
    isNote = false,
    referenceStepLabel,
    className,
  },
  readinessRef,
) {
  const { t } = useI18n();
  const pathLabel = usePathLabel(currency, isNote);
  // Block 11 (AC-N2): the reference step's issues are collapsed into one step link (name + count);
  // they never appear as per-issue `references:*` message text in the rail.
  const refIssues = referenceStepLabel
    ? issues.filter((issue) => stepOfField(issue.path) === 'reference')
    : [];
  const firstRefIssue = refIssues[0];
  const listIssues = referenceStepLabel
    ? issues.filter((issue) => stepOfField(issue.path) !== 'reference')
    : issues;
  // Block 11 (B11-CN-03): the credit cap. The four numbers come from `creditCapIssue`'s params
  // ({cap, otherCredits, remaining, remainingForeign}), carried on the `exceeds_reference` issue.
  // The display shows «المتبقي القابل للتخفيض» before this note (= `remaining`) and after it
  // (= `remaining` − the note's total), in EGP and at the note's own rate.
  const capIssue = issues.find(
    (issue) => issue.path === 'totalAmount' && issue.issue === 'exceeds_reference',
  );
  const remaining = capIssue?.params?.remaining;
  const remainingForeign = capIssue?.params?.remainingForeign;
  const afterRemaining =
    remaining !== undefined && result?.totalAmount !== undefined
      ? dec(String(remaining)).minus(dec(result.totalAmount)).toFixed(5)
      : null;
  const afterForeign =
    remainingForeign !== undefined && amountDueForeign
      ? dec(String(remainingForeign)).minus(dec(amountDueForeign)).toFixed(5)
      : null;
  return (
    <aside
      id="editor-summary"
      className={`${styles.rail} ${className ?? ''}`}
      aria-labelledby="editor-summary-title"
      data-testid="editor-summary"
    >
      <h2 id="editor-summary-title" className={styles.railTitle}>
        {isNote ? t.editor.summary.labelNote : t.editor.summary.label}
      </h2>
      <div className={styles.numberChip} data-testid="number-chip">
        <span>{t.editor.number}</span>
        <strong data-testid="editor-number">{number ?? nextNumber ?? '—'}</strong>
        {!number ? (
          <span data-testid="number-provisional">{t.editor.numberProvisional}</span>
        ) : null}
      </div>

      <section
        ref={readinessRef}
        tabIndex={-1}
        className={styles.railSection}
        aria-labelledby="editor-readiness-title"
        data-testid="readiness-card"
      >
        <h3 id="editor-readiness-title" className={styles.railHeading}>
          {t.editor.sections.readiness}
        </h3>
        <div className={styles.readiness}>
          <span
            className={`${styles.readinessHead} ${issues.length ? styles.readinessBad : styles.readinessOk}`}
            data-testid="readiness-badge"
            data-readiness={issues.length ? 'incomplete' : 'ready'}
          >
            {issues.length ? <CircleAlert size={16} /> : <CircleCheck size={16} />}
            {issues.length
              ? t.editor.readiness.incomplete(issues.length)
              : t.editor.readiness.ready}
          </span>
          <p className={styles.note} style={{ marginBlockStart: 0 }}>
            {isNote ? t.editor.readiness.localOnlyNote : t.editor.readiness.localOnly}
          </p>
          {issues.length ? (
            <ul className={styles.issueList} data-testid="readiness-issues">
              {firstRefIssue ? (
                <li key="reference-step">
                  <button
                    type="button"
                    data-testid="issue-references"
                    onClick={() => onIssue(firstRefIssue)}
                  >
                    <CircleAlert size={14} aria-hidden="true" />
                    <span>
                      <strong>{referenceStepLabel}</strong>{' '}
                      {t.editor.readiness.incomplete(refIssues.length)}
                    </span>
                  </button>
                </li>
              ) : null}
              {listIssues.map((issue, k) => (
                <li key={`${issue.path}-${issue.issue}-${k}`}>
                  {issue.path === 'organization.rin' ? (
                    <Link to="/orgs/$orgId" params={{ orgId }} className={styles.warnItem}>
                      <CircleAlert size={14} aria-hidden="true" />
                      <span>
                        <strong>{pathLabel(issue.path)}:</strong> {issueMessage(t, issue)}
                      </span>
                    </Link>
                  ) : (
                    <button
                      type="button"
                      data-testid={`issue-${issue.path}`}
                      data-issue={issue.issue}
                      onClick={() => onIssue(issue)}
                    >
                      <CircleAlert size={14} aria-hidden="true" />
                      <span>
                        <strong>{pathLabel(issue.path)}:</strong> {issueMessage(t, issue)}
                      </span>
                    </button>
                  )}
                </li>
              ))}
            </ul>
          ) : null}
          {warnings.length ? (
            <>
              <span className={styles.sectionLabel} style={{ margin: 0 }}>
                {t.editor.readiness.warnings}
              </span>
              {warnings.map((w, k) => (
                <div
                  key={`${w.path}-${k}`}
                  className={styles.warnItem}
                  data-testid={`warning-${w.issue}`}
                >
                  <Info size={14} aria-hidden="true" />
                  <span>{issueMessage(t, w)}</span>
                </div>
              ))}
            </>
          ) : null}
        </div>
      </section>

      <section className={styles.railSection} aria-labelledby="editor-totals-title">
        <h3 id="editor-totals-title" className={styles.railHeading}>
          {t.editor.sections.totals}
        </h3>
        <Totals
          result={result}
          currency={currency}
          amountDueForeign={amountDueForeign}
          isCredit={isCredit}
        />
        <p className={styles.note}>{t.editor.totals.egpNote}</p>
      </section>

      {capIssue && remaining !== undefined ? (
        <section
          className={styles.railSection}
          aria-labelledby="editor-reducible-title"
          data-testid="reducible"
        >
          <h3 id="editor-reducible-title" className={styles.railHeading}>
            {t.editor.note.stillReducible}
          </h3>
          <dl className={styles.totals} data-testid="reducible-amounts">
            <div className={styles.totalRow}>
              <dt>{t.editor.note.reducibleBefore}</dt>
              <dd>
                <Money value={String(remaining)} currency="EGP" testId="reducible-before" />
                {remainingForeign !== undefined && currency !== 'EGP' ? (
                  <span className={styles.reviewSub}>
                    <Money
                      value={String(remainingForeign)}
                      currency={currency}
                      testId="reducible-before-foreign"
                    />
                  </span>
                ) : null}
              </dd>
            </div>
            <div className={styles.totalRow}>
              <dt>{t.editor.note.reducibleAfter}</dt>
              <dd>
                <Money value={afterRemaining} currency="EGP" testId="reducible-after" />
                {afterForeign !== null && currency !== 'EGP' ? (
                  <span className={styles.reviewSub}>
                    <Money
                      value={afterForeign}
                      currency={currency}
                      testId="reducible-after-foreign"
                    />
                  </span>
                ) : null}
              </dd>
            </div>
          </dl>
        </section>
      ) : null}
    </aside>
  );
});

/**
 * Below 64rem the rail shows as this one-line summary: the number, the amount due and the open
 * issues. It expands the rail in place. It sits outside the rail, which itself holds no toggle
 * (AC-E1).
 */
export function SummaryToggle({
  number,
  amountDue,
  issues,
  isExpanded,
  onToggle,
}: {
  number: string | null;
  amountDue: string | null;
  issues: number;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const { t, formatAmount } = useI18n();
  return (
    <button
      type="button"
      className={styles.summaryToggle}
      aria-expanded={isExpanded}
      aria-controls="editor-summary"
      onClick={onToggle}
      data-testid="editor-summary-toggle"
    >
      <span className={styles.mono}>{number ?? '—'}</span>
      <span aria-hidden="true">·</span>
      <span>{amountDue ? formatAmount(amountDue, 'EGP') : '—'}</span>
      <span className={issues ? styles.toggleIssues : styles.toggleClean}>
        {issues ? (
          <CircleAlert size={16} aria-hidden="true" />
        ) : (
          <CircleCheck size={16} aria-hidden="true" />
        )}
        <span className="visually-hidden">
          {issues ? t.editor.readiness.incomplete(issues) : t.editor.readiness.ready}
        </span>
        {issues ? <span aria-hidden="true">{issues}</span> : null}
      </span>
      <ChevronDown size={16} aria-hidden="true" className={styles.toggleChevron} />
    </button>
  );
}

function Totals({
  result,
  currency,
  amountDueForeign,
  isCredit = false,
}: {
  result: PreviewResult | undefined;
  currency: string;
  amountDueForeign: string | null;
  /** Block 11 (role-4 B-1): a credit note flips the due-box caption, not its value. */
  isCredit?: boolean;
}) {
  const { t } = useI18n();
  if (!result) return <p className={styles.note}>{t.editor.totals.waiting}</p>;
  const taxMap = new Map(result.taxTotals.map((x) => [x.taxType, x.amount]));
  // D-7 / gate 3.07: T1 and T4 always shown; other types when present; deductions captioned.
  for (const tt of ['T1', 'T4']) if (!taxMap.has(tt)) taxMap.set(tt, '0.00000');
  const taxRows = [...taxMap.entries()].sort(
    (a, b) => Number(a[0].slice(1)) - Number(b[0].slice(1)),
  );
  const row = (label: ReactNode, value: string, testId: string, deducted = false) => (
    <div className={styles.totalRow} key={testId}>
      <dt>
        {label}
        {deducted ? <span className={styles.deduct}>({t.editor.totals.deducted})</span> : null}
      </dt>
      <dd>
        <Money value={value} currency="EGP" testId={testId} />
      </dd>
    </div>
  );
  return (
    <dl className={styles.totals} data-testid="totals">
      {row(t.editor.totals.salesTotal, result.totalSalesAmount, 'total-sales')}
      {row(t.editor.totals.discount, result.totalDiscountAmount, 'total-discount', true)}
      {row(t.editor.totals.net, result.netAmount, 'total-net')}
      {taxRows.map(([tt, amount]) =>
        row(<TaxLabel code={tt} />, amount, `total-tax-${tt}`, tt === 'T4'),
      )}
      {row(
        t.editor.totals.itemsDiscount,
        result.totalItemsDiscountAmount,
        'total-items-discount',
        true,
      )}
      {row(t.editor.totals.extraDiscount, result.extraDiscountAmount, 'total-extra-discount', true)}
      <div className={styles.grand}>
        <span>{isCredit ? t.editor.totals.amountDueCredit : t.editor.totals.amountDue}</span>
        <strong>
          <Money value={result.totalAmount} currency="EGP" testId="total-amount" />
        </strong>
        {currency !== 'EGP' && amountDueForeign ? (
          <span data-testid="total-foreign">
            {isCredit
              ? t.editor.totals.amountDueCreditForeign(currency)
              : t.editor.totals.amountDueForeign(currency)}
            : <Money value={amountDueForeign} currency={currency} testId="total-amount-foreign" />
          </span>
        ) : null}
      </div>
    </dl>
  );
}

/** Official ETA tax-type label (reference table), with its code. */
export function TaxLabel({ code }: { code: string }) {
  const { pick } = useI18n();
  const types = useQuery(taxTypesQuery);
  const tt = types.data?.find((x) => x.code === code);
  return (
    <>
      {tt ? pick(tt.nameAr, tt.nameEn) : code} <span className={styles.subtle}>({code})</span>
    </>
  );
}
